import { useState, useRef, useEffect, ReactNode, MouseEvent } from 'react';
import { BellIcon, CheckIcon, ReloadIcon } from '@radix-ui/react-icons';
import { supabase, handleNetworkError } from '../lib/supabase';
import type { Notification } from '../lib/types';
import { 
  Box,
  Flex,
  Heading,
  Text
} from '@radix-ui/themes';
import { createPortal } from 'react-dom';
import { ArrowDownCircle, LogIn, LogOut, Clock } from 'lucide-react';
import { useNavigate } from 'react-router';

// Componente HTML puro senza ref complessi
interface SimpleButtonProps {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  style?: React.CSSProperties;
}

function SimpleButton({ children, ...props }: SimpleButtonProps) {
  return (
    <button
      type="button"
      style={{
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        borderRadius: '4px',
        position: 'relative',
        ...(props.style || {})
      }}
      onClick={props.onClick}
    >
      {children}
    </button>
  );
}

// Funzione per determinare l'icona appropriata in base al titolo della notifica
const getNotificationIcon = (title: string, isDarkMode: boolean) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('ingresso') || lowerTitle.includes('entrata')) {
    return <LogIn size={16} color={isDarkMode ? "var(--green-11)" : "var(--green-9)"} style={{ marginRight: '2px' }} />;
  } else if (lowerTitle.includes('uscita')) {
    return <LogOut size={16} color={isDarkMode ? "var(--blue-11)" : "var(--blue-9)"} style={{ marginRight: '2px' }} />;
  } else if (lowerTitle.includes('registrato')) {
    return <Clock size={16} color={isDarkMode ? "var(--amber-11)" : "var(--amber-9)"} style={{ marginRight: '2px' }} />;
  } else {
    return <ArrowDownCircle size={16} color={isDarkMode ? "var(--accent-11)" : "var(--accent-9)"} style={{ marginRight: '2px' }} />;
  }
};

type Props = {
  userId: string;
};

export default function NotificationBell({ userId }: Props) {
  const navigate = useNavigate();
  
  // Evita di usare ref e useEffect problematici
  const [isVisible, setIsVisible] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Determina se la modalità scura è attiva
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark') || 
                    document.documentElement.getAttribute('data-theme') === 'dark' ||
                    window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };
    
    checkDarkMode();
    
    // Osserva i cambiamenti dell'attributo class sul documentElement
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class', 'data-theme'] 
    });
    
    return () => observer.disconnect();
  }, []);

  // Gestione click fuori dal dropdown per chiuderlo
  useEffect(() => {
    if (!isVisible) return;
    
    const handleClickOutside = (event: MouseEvent | any) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  // Aggiorna le notifiche ogni 60 secondi quando il dropdown è aperto
  useEffect(() => {
    if (!isVisible) return;
    
    const intervalId = setInterval(() => {
      if (isVisible) {
        handleRetry();
      }
    }, 60000); // Aggiorna ogni minuto
    
    return () => clearInterval(intervalId);
  }, [isVisible, userId]);

  // Funzione semplice per gestire il toggle manuale
  const toggleDropdown = () => {
    const newState = !isVisible;
    setIsVisible(newState);
    
    // Aggiorna la posizione del dropdown quando viene aperto
    if (newState && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: Math.min(rect.left, window.innerWidth - 366)
      });
    }
    
    // Carica i dati solo quando serve, evitando cicli
    if (newState && !isInitialized) {
      setIsInitialized(true);
      loadNotifications();
    } else if (newState) {
      // Se il dropdown viene riaperto, ricarica i dati
      handleRetry();
    }
  };

  const loadNotifications = () => {
    setIsLoading(true);
    setErrorMsg(null);
    
    try {
      supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50)
        .then(({ data, error }) => {
          if (error) {
            console.error('Error fetching notifications:', error);
            setErrorMsg(handleNetworkError(error));
          } else {
            setNotifications(data || []);
            setErrorMsg(null);
          }
          setIsLoading(false);
        });
    } catch (err) {
      console.error('Error in notification fetch:', err);
      setErrorMsg('Errore durante il caricamento delle notifiche');
      setIsLoading(false);
    }
  };

  const handleNotificationClick = (notificationId: string, e: MouseEvent) => {
    e.stopPropagation();

    // Marca come letta
    try {
      supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .then(() => {
          setNotifications(prev =>
            prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
          );
        });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }

    // Chiudi dropdown e naviga alla pagina notifiche
    setIsVisible(false);
    navigate('/notifications');
  };

  const handleMarkAllAsRead = (e: MouseEvent) => {
    e.stopPropagation(); // Evita che il click si propaghi al contenitore
    
    try {
      supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false)
        .then(() => {
          setNotifications(prev => 
            prev.map(n => ({ ...n, read: true }))
          );
        });
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleRetry = (e?: MouseEvent) => {
    if (e) e.stopPropagation();
    
    setIsLoading(true);
    setErrorMsg(null);
    loadNotifications();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleViewAllNotifications = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(false);
    // Utilizzo diretto di window.location per la navigazione
    window.location.href = '/notifications';
  };

  // Renderizza il dropdown come portale nel body
  const renderDropdown = () => {
    if (!isVisible) return null;
    
    return createPortal(
      <div 
        style={{ 
          position: 'fixed', 
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: '350px',
          maxHeight: '80vh',
          overflow: 'auto',
          backgroundColor: isDarkMode ? 'var(--gray-2)' : 'white',
          borderRadius: '8px',
          boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 2147483647,
          border: `1px solid ${isDarkMode ? 'var(--gray-4)' : 'var(--gray-5)'}`,
          transform: 'translateZ(0)',
          color: isDarkMode ? 'var(--gray-12)' : 'var(--gray-12)'
        }}
        className="notification-dropdown"
        onClick={(e) => e.stopPropagation()} // Evita che i click interni chiudano il dropdown
      >
        <Box p="4" style={{ borderBottom: `1px solid ${isDarkMode ? 'var(--gray-4)' : 'var(--gray-4)'}` }}>
          <Flex justify="between" align="center">
            <Heading size="3">Notifiche</Heading>
            <Flex gap="2">
              <SimpleButton onClick={(e) => handleRetry(e)}>
                <ReloadIcon width="14" height="14" />
              </SimpleButton>
              {unreadCount > 0 && (
                <SimpleButton onClick={(e) => handleMarkAllAsRead(e)}>
                  <CheckIcon width="14" height="14" style={{ marginRight: '4px' }} />
                  Segna tutte
                </SimpleButton>
              )}
            </Flex>
          </Flex>
        </Box>

        <Box>
          {errorMsg ? (
            <Box p="4">
              <Text align="center" color="red" size="2" mb="2">
                {errorMsg}
              </Text>
              <SimpleButton 
                onClick={(e) => handleRetry(e)}
                style={{ width: '100%' }}
              >
                <ReloadIcon width="14" height="14" style={{ marginRight: '4px' }} />
                Riprova
              </SimpleButton>
            </Box>
          ) : isLoading ? (
            <Box p="4">
              <Text align="center" color="gray" size="2">
                Caricamento...
              </Text>
            </Box>
          ) : notifications.length === 0 ? (
            <Box p="4">
              <Text align="center" color="gray" size="2">
                Nessuna notifica
              </Text>
            </Box>
          ) : (
            notifications.slice(0, 10).map(notification => {
              const isIngresso = notification.title.toLowerCase().includes('ingresso');
              const isUscita = notification.title.toLowerCase().includes('uscita');
              const iconBgColor = isIngresso 
                ? isDarkMode ? 'var(--green-4)' : 'var(--green-3)'
                : isUscita 
                  ? isDarkMode ? 'var(--blue-4)' : 'var(--blue-3)'
                  : isDarkMode ? 'var(--gray-5)' : 'var(--gray-3)';
                
              return (
                <div
                  key={notification.id}
                  onClick={(e: any) => handleNotificationClick(notification.id, e)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${isDarkMode ? 'var(--gray-4)' : 'var(--gray-4)'}`,
                    backgroundColor: notification.read 
                      ? 'transparent' 
                      : isDarkMode ? 'var(--blue-3)' : 'var(--blue-2)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <Flex justify="between" align="center" gap="3">
                    <Flex gap="3" align="center" style={{ width: '100%' }}>
                      <Box style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: iconBgColor,
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>
                        {getNotificationIcon(notification.title, isDarkMode)}
                      </Box>
                      <Box style={{ marginLeft: '8px', flex: 1 }}>
                        <Text weight="medium" size="2" as="div" style={{ marginBottom: '6px' }}>
                          {notification.title}
                        </Text>
                        <Text color={isDarkMode ? "gray" : "gray"} size="1" as="div" style={{ marginBottom: '6px', lineHeight: '1.4' }}>
                          {notification.message?.length > 100 
                            ? notification.message.substring(0, 100) + '...' 
                            : notification.message}
                        </Text>
                        <Text color={isDarkMode ? "gray" : "gray"} size="1" as="div">
                          {new Date(notification.created_at).toLocaleString('it')}
                        </Text>
                      </Box>
                    </Flex>
                    {!notification.read && (
                      <Box 
                        style={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: 'var(--accent-9)',
                          flexShrink: 0
                        }} 
                      />
                    )}
                  </Flex>
                </div>
              );
            })
          )}
        </Box>
      </div>,
      document.body
    );
  };

  // Rendering completamente statico
  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      {/* Bottone trigger */}
      <SimpleButton onClick={toggleDropdown}>
        <BellIcon width="20" height="20" />
        {unreadCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              backgroundColor: 'var(--accent-9)',
              color: 'white',
              borderRadius: '9999px',
              padding: '2px 6px',
              fontSize: '12px',
              lineHeight: 1,
              fontWeight: 500
            }}
          >
            {unreadCount}
          </div>
        )}
      </SimpleButton>

      {/* Renderizza il dropdown come portale */}
      {renderDropdown()}
    </div>
  );
}